import { useParams, Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { Reveal } from "@/components/motion/Reveal";
import Breadcrumbs from "@/components/Breadcrumbs";
import { work, caseStudyDetail, VERIFY } from "@/data/content";
import { caseStudySchema } from "@/data/seo";
import NotFound from "@/pages/NotFound";

function Block({ label, value }) {
  if (value === VERIFY) return null;
  return (
    <div className="grid gap-3 border-t border-line py-10 md:grid-cols-12 md:gap-8">
      <h3 className="font-display text-xl font-extrabold uppercase tracking-wide text-ink md:col-span-3">{label}</h3>
      <div className="md:col-span-9">
        <p className="max-w-xl text-lg leading-relaxed text-ink">{value}</p>
      </div>
    </div>
  );
}

export default function CaseStudy() {
  const { slug } = useParams();
  const base = work.find((w) => w.slug === slug);
  const crumbs = base ? [{ name: "Work", path: "/work" }, { name: base.client, path: `/work/${slug}` }] : undefined;
  useSeo({
    title: base ? `${base.client} ${base.title}: ${base.category} Case Study` : "Case Study",
    description: base
      ? `${base.excerpt} A ${base.category.toLowerCase()} case study by HashTag Marketing, integrated marketing agency in Gurgaon, Delhi NCR. Services: ${base.services.join(", ")}.`
      : "Case study",
    path: `/work/${slug}`,
    image: base?.image,
    type: "article",
    pageType: "ItemPage",
    noindex: !base,
    breadcrumbs: crumbs,
    schema: base ? [caseStudySchema(base)] : [],
  });
  if (!base) return <NotFound />;
  const cs = caseStudyDetail(base);
  const others = work.filter((w) => w.slug !== slug).slice(0, 2);

  return (
    <>
      <section className="border-b border-line pt-24 sm:pt-28 md:pt-32 lg:pt-36">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 md:px-10 lg:px-12 xl:px-16">
          <Breadcrumbs items={[{ name: "Work", path: "/work", testId: "back-to-work" }, { name: cs.client, path: `/work/${slug}` }]} />
          <div className="grid gap-6 pb-12 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <p className="mb-4 text-xs font-bold uppercase tracking-[0.22em] text-accent">{cs.category}</p>
              <h1 className="font-display text-6xl font-extrabold leading-[0.9] tracking-tight text-ink md:text-8xl">{cs.client}</h1>
              <p className="mt-4 font-serif text-2xl italic text-muted md:text-3xl">{cs.title}{cs.year ? ` · ${cs.year}` : ""}</p>
            </div>
            <div className="flex flex-col justify-end lg:col-span-3 lg:col-start-10">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-muted">Services</p>
              <ul className="mt-3 space-y-1 text-ink">
                {cs.services.map((s) => <li key={s} className="text-lg font-medium">{s}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 md:px-10 lg:px-12 xl:px-16">
        <Reveal className="my-12 aspect-[16/9] overflow-hidden bg-line md:my-16">
          <img src={cs.image} alt={`${cs.client} — ${cs.title}`} className="img-cover" />
        </Reveal>
      </div>

      <section className="mx-auto max-w-[1600px] px-5 pb-16 sm:px-8 md:px-10 lg:px-12 xl:px-16">
        <Block label="Challenge" value={cs.challenge} />
        <Block label="Insight" value={cs.insight} />
        <Block label="Strategy" value={cs.strategy} />
        <Block label="Execution" value={cs.execution} />
        {cs.gallery.length > 0 && (
          <div className="border-t border-line py-10" data-testid="case-gallery">
            <h3 className="mb-8 font-display text-xl font-extrabold uppercase tracking-wide text-ink">Gallery</h3>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              {cs.gallery.map((src, i) => (
                <div key={src} className="aspect-[4/3] overflow-hidden bg-line">
                  <img
                    src={src}
                    alt={`${cs.client} gallery ${i + 1}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 hover:scale-[1.04]"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="border-t border-line bg-surface">
        <div className="mx-auto max-w-[1600px] px-5 py-14 sm:py-16 md:py-20 lg:py-24 xl:py-28 sm:px-8 md:px-10 lg:px-12 xl:px-16">
          <div className="mb-8 flex items-end justify-between md:mb-10">
            <h2 className="font-display text-3xl font-extrabold text-ink md:text-5xl">More work</h2>
            <Link to="/work" className="group inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-ink">
              View all <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
          <div className="grid gap-x-6 gap-y-10 md:grid-cols-2">
            {others.map((o, i) => (
              <Link key={o.slug} to={`/work/${o.slug}`} className="group block" data-testid={`related-${o.slug}`}>
                <div className="relative aspect-[4/3] overflow-hidden bg-line">
                  <img src={o.image} alt={o.client} loading="lazy" className="img-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <h3 className="mt-4 font-display text-2xl font-extrabold text-ink">{o.client}</h3>
                <p className="text-sm text-muted">{o.title}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
